import { arcPath, dayLabel, niceMax, sayCount, seriesColor, slices } from '../lib/charts';

// ---------------------------------------------------------------------------
// Three small charts (205), drawn as SVG on the theme's tokens so they
// follow the palette and both modes without a library. Each is a picture
// of a table the page also shows, so nothing is only in the picture.
// ---------------------------------------------------------------------------

/** Credits by day: one bar a day, the axis rounded to a whole number, the month named where it starts. */
export function DayBars({ rows, height = 120 }: { rows: Array<{ day: string; credits: number }>; height?: number }) {
  const max = niceMax(Math.max(0, ...rows.map((r) => r.credits)));
  const W = 600, H = height, pad = 18, gap = 2;
  const n = Math.max(1, rows.length);
  const bw = (W - pad * 2) / n;
  const every = n > 40 ? 7 : n > 14 ? 2 : 1;
  return (
    <figure className="chart" data-tool="chart-days">
      <svg viewBox={`0 0 ${W} ${H + 22}`} role="img" aria-label={`Credits spent each day for the last ${n} days, up to ${sayCount(max)} a day`}>
        {[0, 0.5, 1].map((t) => (
          <g key={t}>
            <line x1={pad} x2={W - pad} y1={H - t * (H - 8)} y2={H - t * (H - 8)} className="chart-grid" />
            <text x={pad - 4} y={H - t * (H - 8) + 3} className="chart-axis" textAnchor="end">{sayCount(max * t)}</text>
          </g>
        ))}
        {rows.map((r, i) => {
          const h = max > 0 ? (r.credits / max) * (H - 8) : 0;
          return (
            <g key={r.day}>
              <rect x={pad + i * bw + gap / 2} y={H - h} width={Math.max(1, bw - gap)} height={h} rx={2} className="chart-bar"><title>{`${r.day}: ${sayCount(r.credits)} credits`}</title></rect>
              {(i % every === 0 || i === n - 1) && <text x={pad + i * bw + bw / 2} y={H + 14} className="chart-axis" textAnchor="middle">{dayLabel(r.day, i === 0)}</text>}
            </g>
          );
        })}
      </svg>
    </figure>
  );
}

/** A ring of shares with its legend. The centre says the total, or whatever the caller wants said. */
export function Donut({ parts, centre, size = 132 }: { parts: Array<{ label: string; value: number }>; centre?: { big: string; small?: string }; size?: number }) {
  const sl = slices(parts.map((p) => p.value));
  const r = 40, cx = 50, cy = 50;
  return (
    <figure className="chart donut" data-tool="chart-donut">
      <svg viewBox="0 0 100 100" width={size} height={size} role="img" aria-label={parts.map((p, i) => `${p.label} ${Math.round((sl[i]?.share ?? 0) * 100)}%`).join(', ')}>
        <circle cx={cx} cy={cy} r={r} className="chart-ring" />
        {sl.map((s, i) => s.share > 0 && <path key={i} d={arcPath(cx, cy, r, s.start, s.end)} className="chart-arc" style={{ stroke: seriesColor(i) }}><title>{`${parts[i]!.label}: ${sayCount(parts[i]!.value)}`}</title></path>)}
        {centre && <>
          <text x={cx} y={cy + (centre.small ? 1 : 4)} className="chart-centre" textAnchor="middle">{centre.big}</text>
          {centre.small && <text x={cx} y={cy + 12} className="chart-centre-small" textAnchor="middle">{centre.small}</text>}
        </>}
      </svg>
      <ul className="chart-legend">
        {parts.map((p, i) => (
          <li key={p.label}><span className="chart-swatch" style={{ background: seriesColor(i) }} />{p.label}<b>{Math.round((sl[i]?.share ?? 0) * 100)}%</b></li>
        ))}
      </ul>
    </figure>
  );
}

/** Bars by name, longest first, each with its number; the first colour for all, so the eye reads length and not hue. */
export function NameBars({ rows, onPick }: { rows: Array<{ id?: string; label: string; sub?: string; value: number }>; onPick?: (id: string) => void }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ol className="chart namebars" data-tool="chart-names">
      {rows.map((r, i) => (
        <li key={`${r.label}-${i}`}>
          <span className="namebar-label">
            {r.id && onPick ? <button type="button" className="linkish" onClick={() => onPick(r.id!)} title="About this model">{r.label}</button> : r.label}
            {r.sub && <span className="sub-dim"> · {r.sub}</span>}
          </span>
          <span className="namebar-track"><span className="namebar-fill" style={{ width: `${(r.value / max) * 100}%`, background: seriesColor(0) }} /></span>
          <span className="namebar-num">{sayCount(r.value)}</span>
        </li>
      ))}
    </ol>
  );
}
